<template>
  <div>
    <NsMinimalHeader v-if="loaded && showMinimalHeader" v-bind="$props" />
    <NsHeaderAem v-if="loaded && !showMinimalHeader" v-bind="$props" />
  </div>
</template>
<script>
import NsHeaderAem from "./NsHeaderAem.vue";
import NsMinimalHeader from "./NsMinimalHeader.vue";
import { StoreFrontSponsorStorageService } from "@nuskin/ns-util";
import {
  overrideConfigurationProperty,
  getConfiguration
} from "@nuskin/configuration-sdk";
window.overrideContentStackConfig =
  window.overrideContentStackConfig || overrideConfigurationProperty;
export default {
  name: "NsHeader",
  components: {
    NsHeaderAem,
    NsMinimalHeader
  },
  props: {
    /**
     * Allows you to set the image for the header (distributor profile image).
     * You must also set either hrefOverride (can be done in the service) or logoUrl for this to show
     */
    distributorImage: {
      type: String,
      default: ""
    },
    /**
     * Hides the cart
     */
    hideCart: Boolean,
    /**
     * Hides the entire header
     */
    hideHeader: Boolean,
    /**
     * Hides the top left section
     */
    hideTopLeft: Boolean,
    /**
     * Hides the bottom left section
     */
    hideBottomLeft: Boolean,
    /**
     * Hides the top right section
     */
    hideTopRight: Boolean,
    /**
     * Hides the bottom right section
     */
    hideBottomRight: Boolean,
    /**
     * Hides the bottom center section
     */
    hideBottomCenter: Boolean,
    /**
     * Hides the top center section
     */
    hideTopCenter: Boolean,
    /**
     * Hides profile component (Sign Up/Sign In)
     */
    hideProfile: Boolean,
    /**
     * Hides the search bar
     */
    hideSearchbar: Boolean,
    /**
     * Hides everything in the utility bar
     */
    hideUtilityBar: Boolean,
    /**
     * Hides the legal links
     */
    hideLegalLinks: Boolean,
    /**
     * Hides the marketing message
     */
    hideMarketingMessage: Boolean,
    /**
     * Hides the region selector
     */
    hideRegionSelector: Boolean,
    /**
     * Hides the menu
     */
    hideMenu: Boolean,
    /**
     * Hides the logo
     */
    hideLogo: Boolean,
    /**
     * Hides the top navigation
     */
    hideTopNav: Boolean,
    /**
     * Hides loyalty
     */
    hideLoyalty: Boolean,
    /**
     * Hides Join Us
     */
    hideJoinUs: Boolean,
    /**
     * Hides Sign Up
     */
    hideSignUp: Boolean,
    /**
     * If set, this will override the page redirect on the region selector page and the distributor logo (and logout redirect)
     */
    hrefOverride: {
      type: String,
      default: ""
    },
    /**
     * Allows you to set the id for the header component
     */
    id: {
      type: String,
      default: ""
    },
    /**
     * Boolean for if menu is open, toggles state of other elements accordingly
     */
    isMenuOpen: Boolean,
    /**
     * Translation and link for Join Us link
     */
    joinUs: {
      type: [Object, String],
      default: null
    },
    /**
     * Link for UsableNet accessibility
     */
    accessibilityLink: {
      type: [Object, String],
      default: null
    },
    /**
     * Array of links to display in the top utility bar: left
     * Objects in array require 'href' and 'name'
     */
    legalLinks: {
      type: [Array, String],
      default: null
    },
    /**
     * Set the URL that you are redirected to when clicking on the header/distributor logo
     */
    logoUrl: {
      type: String,
      default: ""
    },
    /**
     * Sets information in the top utility bar: center
     * Object requires 'info', 'href', and 'link'
     */
    marketingMessage: {
      type: [Object, String],
      default: null
    },
    /**
     * Used in conjunction with useHeaderService, setting this to true
     * will make the service return data for a My Site header (storefront, personal offer).
     */
    mysite: Boolean,
    /**
     * Used to force the component to ignore My Site header data.
     */
    noMysite: Boolean,
    /**
     * Navigation array for the mega dropdown
     */
    navigation: {
      type: [Array, String],
      default: null
    },
    /**
     * Array of menu items for the profile component
     */
    profileMenu: {
      type: [Array, String],
      default: null
    },
    /**
     * Translations for the profile
     */
    profileTranslations: {
      type: [Object, String],
      default: null
    },
    /**
     * Region
     */
    region: {
      type: [Object, String],
      default: null
    },
    /**
     * Allows you to set where the select other region redirects too
     */
    regionSelectorPath: {
      type: String,
      default: ""
    },
    /**
     * If set, this will only show regions according to what is in the array
     * [ "en_US", "es_US" ]
     */
    regionsAllowed: {
      type: String,
      default: ""
    },
    /**
     * Translations for the search results
     */
    searchResultsTranslations: {
      type: [Object, String],
      default: null
    },
    /**
     * Where to redirect after signing out
     */
    signOutUrl: {
      type: String,
      default: ""
    },
    /**
     * Toggles theme styles and functionality
     */
    theme: {
      type: String,
      default: "auto"
    },
    /**
     * Translations for the header
     * Options: 'selectOther'
     */
    translations: {
      type: [Object, String],
      default: null
    },
    /**
     * Don't use the AEM service to pull all the information needed to use the header
     */
    noHeaderService: Boolean,
    /**
     * Allows tabs for mega dropdown to be clickable to top-level category pages
     * Should only be used for dev purposes, as it is incompatible with touch
     * devices
     */
    useTopLevelNav: Boolean,
    /**
     * use a market override for the logo url source for <NsLogo> in the header
     *   reference the computed prop marketOverrideCondition
     *   if marketOverrideCondition resolves to true, assign the props `:market-logo-mobile-url`
     *   and `:market-logo-desktop-url` given to <NsLogo/> to be the market override logo source urls
     */
    logoUrlMarketOverride: {
      type: Boolean,
      default: false
    },
    /**
     * For disabling href link on logo
     */
    disableLogoLink: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loaded: false,
      isEquinoxMarket: false,
      isMySite: false,
      contentStackEnvironment: process.env.VUE_APP_CONTENT_STACK_ENVIRONMENT
    };
  },
  computed: {
    showMinimalHeader() {
      // Never use the minimal header on mysite or non-equinox markets
      return (
        !window.location.href.includes("mysite") &&
        !window.location.href.includes("mynuskin") &&
        !this.isMySite &&
        this.isEquinoxMarket &&
        !this.mysite &&
        !this.isStorefront
      );
    }
  },
  async beforeCreate() {
    try {
      this.isMySite = !!StoreFrontSponsorStorageService.getStoreFrontSponsor();
      this.isEquinoxMarket =
        (await getConfiguration(["Equinox_Markets"])).Equinox_Markets.active ||
        false;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("headerissue:", e);
    }
    this.loaded = true;
  }
};
</script>
